import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useUsage } from '@/contexts/UsageContext';
import Pressable from '@/components/base/Pressable';

interface DownloadsBarProps {
  lessonTitle: string;
}

const downloadItems = [
  { icon: 'ri-film-line', label: 'Video', key: 'video' },
  { icon: 'ri-file-list-line', label: 'Notes', key: 'notes' },
  { icon: 'ri-stack-line', label: 'Cards', key: 'cards' },
  { icon: 'ri-questionnaire-line', label: 'Quiz', key: 'quiz' },
];

export default function DownloadsBar({ lessonTitle }: DownloadsBarProps) {
  const { resolvedTheme } = useTheme();
  const { tier } = useUsage();
  const isDark = resolvedTheme === 'dark';
  const [showMenu, setShowMenu] = useState(false);
  const [showGate, setShowGate] = useState(false);

  const t = {
    btnBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    btnBorder: isDark ? 'border-white/[0.05]' : 'border-black/[0.05]',
    btnText: isDark ? 'text-[#6E6E73]' : 'text-neutral-500',
    btnHover: isDark ? 'hover:bg-[#222226]' : 'hover:bg-neutral-50',
    menuBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    menuBorder: isDark ? 'border-white/[0.05]' : 'border-black/[0.05]',
    itemIconBg: isDark ? 'bg-white/[0.04]' : 'bg-black/[0.04]',
    itemIcon: isDark ? 'text-[#6E6E73]' : 'text-neutral-400',
    itemText: isDark ? 'text-[#A1A1A6]' : 'text-neutral-600',
    itemSub: isDark ? 'text-[#48484F]' : 'text-neutral-300',
    itemHover: isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.03]',
    lockIcon: isDark ? 'text-neutral-600' : 'text-neutral-300',
  };

  const handleToggle = () => {
    if (!tier.downloadsEnabled) {
      setShowGate(true);
      return;
    }
    setShowMenu(!showMenu);
  };

  return (
    <div className="px-5 pb-4">
      <div className="relative">
        <motion.button
          onClick={handleToggle}
          whileTap={{ scale: 0.95 }}
          className={`w-full py-2.5 rounded-xl ${t.btnBg} border ${t.btnBorder} ${t.btnText} text-xs font-medium flex items-center justify-center gap-2 cursor-pointer ${t.btnHover} transition-colors`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`${tier.downloadsEnabled ? 'ri-download-line' : 'ri-lock-line'} text-xs`} />
          </div>
          {tier.downloadsEnabled ? 'Download & Export' : 'Downloads — Premium Only'}
          {tier.downloadsEnabled && (
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${showMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`} />
            </div>
          )}
        </motion.button>

        <AnimatePresence>
          {showMenu && tier.downloadsEnabled && (
            <motion.div
              initial={{ opacity: 0, y: -6, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -6, height: 0 }}
              transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              className={`absolute top-full left-0 right-0 mt-1.5 rounded-xl ${t.menuBg} border ${t.menuBorder} overflow-hidden z-20`}
            >
              {downloadItems.map((item, i) => (
                <motion.button
                  key={item.key}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  whileTap={{ scale: 0.97 }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left ${t.itemHover} transition-colors cursor-pointer`}
                >
                  <div className={`w-8 h-8 rounded-lg ${t.itemIconBg} flex items-center justify-center`}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${item.icon} ${t.itemIcon} text-sm`} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className={`${t.itemText} text-xs font-medium`}>{item.label}</p>
                    <p className={`${t.itemSub} text-[10px]`}>{lessonTitle}</p>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-download-line ${t.itemSub} text-xs`} />
                  </div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Premium Gate overlay for downloads */}
      <AnimatePresence>
        {showGate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`fixed inset-0 z-[100] ${isDark ? 'bg-[#0F0F10]/70' : 'bg-black/40'} backdrop-blur-md flex items-end sm:items-center justify-center`}
            onClick={() => setShowGate(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 350, damping: 32 }}
              className={`w-full sm:max-w-[380px] sm:rounded-2xl rounded-t-2xl ${isDark ? 'bg-[#1C1D21]' : 'bg-white'} border ${isDark ? 'border-white/[0.05]' : 'border-neutral-100'} overflow-hidden`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 pt-5 pb-3 text-center">
                <div className={`w-14 h-14 rounded-2xl ${isDark ? 'bg-white/[0.05]' : 'bg-neutral-100'} flex items-center justify-center mx-auto mb-3`}>
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className={`ri-download-line ${isDark ? 'text-white' : 'text-neutral-900'} text-2xl`} />
                  </div>
                </div>
                <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Downloads Locked</h3>
                <p className={`text-xs ${isDark ? 'text-neutral-400' : 'text-neutral-500'} mt-1 leading-relaxed max-w-[280px] mx-auto`}>
                  Downloading lessons, notes, and quizzes is a Premium feature. Upgrade to unlock offline access.
                </p>
              </div>
              <div className="px-5 pb-4 space-y-2">
                <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-neutral-50'}`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-line text-red-400 text-sm" />
                  </div>
                  <span className={`text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>Free — Streaming only</span>
                </div>
                <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${isDark ? 'bg-white/[0.04] border border-white/[0.08]' : 'bg-neutral-50 border border-neutral-200'}`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-emerald-500 text-sm" />
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-neutral-900'}`}>Premium — Full downloads</span>
                </div>
              </div>
              <div className="px-5 pb-5 space-y-2.5">
                <Pressable
                  className="w-full bg-neutral-900 text-white py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                  scale={0.97}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-sparkling-line text-sm" />
                  </div>
                  Upgrade to Premium
                </Pressable>
                <button
                  onClick={() => setShowGate(false)}
                  className={`w-full py-2.5 rounded-xl text-xs font-medium ${isDark ? 'text-neutral-500' : 'text-neutral-400'} cursor-pointer hover:opacity-80 transition-opacity`}
                >
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}